const ACTIVE_CLASS = "sticky-atc-active";

// How long after a tap is handled on pointerup to cancel the mouse events
// that may (or may not) follow it.
const GHOST_EVENT_WINDOW = 600;

const SCROLL_FALLBACK = 1000;

// Roughly how long a click's user activation lasts in Chrome and Firefox.
const ACTIVATION_WINDOW = 5000;

/**
 * Mobile add to bag bar. Shows once the form's own actions have scrolled off
 * the top of the viewport, and hides as soon as they're back in view, so only
 * one is ever visible. product-form keeps its buttons in step with the form's.
 *
 * While an option is still unchosen, add to bag instead scrolls to that
 * option and opens it.
 */
class StickyAtc extends HTMLElement {
  connectedCallback() {
    this.productForm = this.closest("product-form");
    const actions = this.productForm?.querySelector(
      "[data-product-form-actions]",
    );

    this.addEventListener("pointerup", this._onPointerUp);
    this.addEventListener("mousedown", this._onGhostMouseDown, {
      capture: true,
    });
    this.addEventListener("click", this._onGhostClick, { capture: true });
    this.addEventListener("click", this._onClick);

    if (!actions) return;

    this.observer = new IntersectionObserver(([entry]) => {
      this._toggle(
        !entry.isIntersecting && entry.boundingClientRect.bottom < 0,
      );
    });
    this.observer.observe(actions);
  }

  disconnectedCallback() {
    this.removeEventListener("pointerup", this._onPointerUp);
    this.removeEventListener("mousedown", this._onGhostMouseDown, {
      capture: true,
    });
    this.removeEventListener("click", this._onGhostClick, { capture: true });
    this.removeEventListener("click", this._onClick);
    this.observer?.disconnect();
    // A colourway swap replaces this element; its successor sets the class
    // again if the bar should still show.
    document.documentElement.classList.remove(ACTIVE_CLASS);
  }

  _toggle(visible) {
    this.classList.toggle("sticky-atc--visible", visible);
    this.inert = !visible;
    document.documentElement.classList.toggle(ACTIVE_CLASS, visible);
  }

  /**
   * iOS Safari keeps the bottom of the screen for its collapsed toolbar: a tap
   * there only expands the toolbar, and never becomes a click on the page. So
   * touch taps are acted on at pointerup, which still counts as user activation
   * for showPicker() and target="_blank". Acting on touchend instead brings
   * the double tap back in that strip.
   */
  _onPointerUp = (event) => {
    if (event.pointerType !== "touch") return;

    const control = event.target.closest(".sticky-atc__button");
    if (!control || control.disabled) return;

    // Touch pointers are captured to the element they went down on, so a
    // finger that slid off the button still ends here.
    const released = document.elementFromPoint(event.clientX, event.clientY);
    if (!control.contains(released)) return;

    this.handledTapAt = event.timeStamp;
    this.isTapClick = true;
    control.click();
    this.isTapClick = false;
  };

  _isGhostEvent(event) {
    return (
      event.isTrusted &&
      this.handledTapAt != null &&
      event.timeStamp - this.handledTapAt <= GHOST_EVENT_WINDOW
    );
  }

  // The tap's mousedown would focus the button, closing the picker _onClick
  // just opened.
  _onGhostMouseDown = (event) => {
    if (this._isGhostEvent(event)) event.preventDefault();
  };

  // Drops the native click when Safari does deliver one, so a tap doesn't add
  // to bag twice.
  _onGhostClick = (event) => {
    if (!this._isGhostEvent(event)) return;

    this.handledTapAt = null;
    event.preventDefault();
    event.stopImmediatePropagation();
  };

  _onClick = (event) => {
    if (!event.target.closest("[data-sticky-atc]")) return;

    const select = [
      ...this.productForm.querySelectorAll(
        "product-form-options select[data-option-index]",
      ),
    ].find((candidate) => !candidate.value);

    if (!select) return;

    event.preventDefault();

    select.scrollIntoView({ behavior: "smooth", block: "center" });
    select.focus({ preventScroll: true });

    // showPicker() needs the click's user activation. A tap opens it at once:
    // iOS's picker is a sheet the scroll doesn't touch, and its activation
    // window is short. A desktop dropdown is anchored to the select and closes
    // when the page scrolls, so it waits for the scroll to end, still inside
    // Chrome and Firefox's ~5s activation window. Unsupported browsers are
    // left with the select focused.
    const open = () => {
      try {
        select.showPicker();
      } catch {}
    };

    if (this.isTapClick) {
      open();
    } else {
      this._afterScroll(open);
    }
  };

  // Browsers without `scrollend` (Safari before 26.2) fall back to a timeout.
  // Where it is supported, it's the only trigger: a long smooth scroll can
  // outlast any fixed timeout, which would open the picker mid-scroll.
  _afterScroll(callback) {
    if (!("onscrollend" in window)) {
      setTimeout(callback, SCROLL_FALLBACK);
      return;
    }

    window.addEventListener("scrollend", callback, { once: true });
    // Past this the picker can't open anyway; don't leave the listener to fire
    // on some later, unrelated scroll.
    setTimeout(
      () => window.removeEventListener("scrollend", callback),
      ACTIVATION_WINDOW,
    );
  }
}

if (!customElements.get("sticky-atc")) {
  customElements.define("sticky-atc", StickyAtc);
}
